import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Download,
  Sparkles 
} from 'lucide-react';
import { Tour } from '../../types';
import { getBrochureDownloadUrl } from '../../utils/brochureUtils';

interface TourCardProps {
  tour: Tour;
  index?: number;
}

const TourCard: React.FC<TourCardProps> = ({ tour, index = 0 }) => {
  const navigate = useNavigate();

  // Handle card click to navigate to tour details
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or links
    const target = e.target as HTMLElement;
    if (target.closest('a') || target.closest('button')) {
      return;
    }
    navigate(`/tours/${tour.tourId}`);
  };

  // Calculate days until deadline
  const calculateDaysToDeadline = (deadline?: string) => {
    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Format price to Indian Rupees
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const daysToDeadline = calculateDaysToDeadline(tour.deadline);
  const isUrgent = daysToDeadline !== null && daysToDeadline <= 7;
  const isExpired = daysToDeadline !== null && daysToDeadline < 0;

  return (
    <motion.div
      className={`card ${tour.isNew ? 'card-luxury' : ''} group cursor-pointer`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.1,
        ease: 'easeOut' 
      }}
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative overflow-hidden">
        <img
          src={tour.imageUrl}
          alt={tour.tourName}
          className="card-image group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col space-y-2">
          {tour.isNew && (
            <motion.span
              className="inline-flex items-center gap-1 badge-accent"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: index * 0.1 + 0.3 }}
            >
              <Sparkles size={14} />
              New
            </motion.span>
          )}
          
          {isUrgent && !isExpired && (
            <span className="px-3 py-1 bg-error text-white text-sm font-semibold rounded-full shadow-lg animate-pulse">
              {daysToDeadline} day{daysToDeadline !== 1 ? 's' : ''} left
            </span>
          )}

          {typeof tour.urgencySlots === 'number' && tour.urgencySlots > 0 && (
            <span className="px-3 py-1 bg-warning text-white text-sm font-semibold rounded-full shadow-lg">
              Only {tour.urgencySlots} slots left
            </span>
          )}
        </div>

        {/* Price Badge */}
        <div className="absolute top-4 right-4">
          <div className="bg-primary-deep-blue bg-opacity-90 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
            <div className="text-lg font-bold">{formatPrice(tour.price)}</div>
            <div className="text-xs opacity-90">per person</div>
          </div>
        </div>

        {/* Hover Overlay */}
        <div className="absolute inset-0 gradient-card-top opacity-0 group-hover:opacity-70 transition-opacity duration-300" />
        
        {/* Quick Actions */}
        <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          <div className="flex justify-between items-center">
            <Link
              to={`/tours/${tour.tourId}`}
              className="btn btn-primary btn-sm"
            >
              View Details
            </Link>
            
            {tour.brochureUrl && (
              <a
                href={getBrochureDownloadUrl(tour.brochureUrl)}
                download
                className="p-2 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-all backdrop-blur-sm"
                title="Download Brochure"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={18} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="card-content">
        {/* Header */}
        <div className="mb-4">
          <h3 className="heading-tertiary text-neutral-charcoal mb-2 group-hover:text-primary-deep-blue transition-colors">
            {tour.tourName}
          </h3>
          
          <div className="flex items-center text-secondary-slate mb-3">
            <MapPin size={16} className="mr-2 text-primary-emerald" />
            <span className="body-small">{tour.destination}</span>
          </div>
        </div>

        {/* Tour Info Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Calendar size={16} className="text-primary-deep-blue" />
            <div>
              <div className="body-small font-medium">Departure</div>
              <div className="text-xs text-secondary-slate">
                {formatDate(tour.departureDate)}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Clock size={16} className="text-primary-deep-blue" />
            <div>
              <div className="body-small font-medium">Duration</div>
              <div className="text-xs text-secondary-slate">
                {tour.durationDays}D/{tour.durationNights}N
              </div>
            </div>
          </div>
        </div>


        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t border-neutral-medium-gray">
          {tour.deadline && (
            <div className="text-center">
              <div className="body-small font-medium text-neutral-charcoal">
                Registration Deadline
              </div>
              <div className={`text-xs ${isUrgent ? 'text-error' : 'text-secondary-slate'}`}>
                {formatDate(tour.deadline)}
              </div>
            </div>
          )}
          
          <button
            className="btn btn-primary btn-sm ml-auto"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/registration?tourId=${tour.tourId}`);
            }}
          >
            Register
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default TourCard;
